import type { ComparisonRequest } from '@/api/runs';
import type {
  ComparisonSelectionTotals,
  SelectionTotalsFile,
} from '@/types/chat/chat';

/**
 * Purpose and roles of a comparison (spec §4, chat-runs-api.md §3.5).
 * Document changes: one baseline among the documents, every other one is
 * compared with it. Check against guidelines: one document to check, every
 * other document is a reference guideline. The request leaves the
 * references out, which the server reads as every other document resolved
 * at submission: a selection that changed meanwhile cannot make it refuse
 * the question (documents_without_role).
 */

export type ComparisonPurpose = 'changes' | 'guidelines';

export interface ComparisonChoice {
  purpose: ComparisonPurpose;
  baselineId: number | null;
  documentToCheckId: number | null;
}

export const EMPTY_CHOICE: ComparisonChoice = {
  purpose: 'changes',
  baselineId: null,
  documentToCheckId: null,
};

/** The documents a role can be given to: usable, duplicates merged. */
export const roleCandidates = (
  totals: ComparisonSelectionTotals | null | undefined,
): SelectionTotalsFile[] =>
  (totals?.files ?? []).filter(
    file => file.usable !== false && file.duplicate_of == null,
  );

export type ComparisonRequestCheck =
  | { ok: true; request: ComparisonRequest }
  | { ok: false; missing: 'baseline' | 'document_to_check' };

/** The request for the current choice: the purpose and the chosen role. */
export const comparisonRequest = (
  choice: ComparisonChoice,
  candidates: SelectionTotalsFile[],
): ComparisonRequestCheck => {
  const has = (id: number | null) =>
    id != null && candidates.some(file => Number(file.id) === id);
  if (choice.purpose === 'changes') {
    return has(choice.baselineId)
      ? {
          ok: true,
          request: {
            purpose: 'changes',
            baseline_file_id: choice.baselineId as number,
          },
        }
      : { ok: false, missing: 'baseline' };
  }
  if (!has(choice.documentToCheckId)) {
    return { ok: false, missing: 'document_to_check' };
  }
  return {
    ok: true,
    request: {
      purpose: 'guidelines',
      document_to_check_id: choice.documentToCheckId as number,
    },
  };
};

/** The other documents of a choice: targets, or reference guidelines. */
export const counterparts = (
  choice: ComparisonChoice,
  candidates: SelectionTotalsFile[],
) => {
  const roleId =
    choice.purpose === 'changes' ? choice.baselineId : choice.documentToCheckId;
  return roleId == null
    ? []
    : candidates.filter(file => Number(file.id) !== roleId);
};

/**
 * The frozen record a pending question shows until the list is loaded
 * again (the shape of the message list's `comparison`).
 */
export const frozenComparison = (
  request: ComparisonRequest,
  candidates: SelectionTotalsFile[],
) => {
  const describe = (id: number) => ({
    id,
    name: candidates.find(file => Number(file.id) === id)?.name ?? null,
  });
  if (request.purpose === 'changes') {
    return {
      purpose: 'changes' as const,
      baseline: describe(request.baseline_file_id),
      targets: candidates
        .map(file => Number(file.id))
        .filter(id => id !== request.baseline_file_id)
        .map(describe),
      document_to_check: null,
      references: [],
      defaulted: false,
    };
  }
  return {
    purpose: 'guidelines' as const,
    baseline: null,
    targets: [],
    document_to_check: describe(request.document_to_check_id),
    references: (
      request.reference_file_ids ??
      candidates
        .map(file => Number(file.id))
        .filter(id => id !== request.document_to_check_id)
    ).map(describe),
    defaulted: false,
  };
};
